import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { StartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useFocusFallbackManualTrigger } from '#src/app/shared/hooks/focus';

type Flow = 'with_ai' | 'write_my_own';

/** An undefined field keeps the normal intro copy. */
type CampaignCopy = { description?: string; primaryActionLabel?: string };

function toCampaignCopy({
	description,
	button,
}: StartAPetitionPageData['featureConfigs']['sapIntroCampaignMelones']): CampaignCopy {
	return {
		description: description.trim() || undefined,
		primaryActionLabel: button.trim() || undefined,
	};
}

/** Undefined keeps the normal intro image and heading. */
type CampaignImage = { path: string; alt: string } | undefined;

type UseIntroResult = ModelHookResult<
	{ copy: CampaignCopy; image: CampaignImage },
	{ submit: (flow: Flow, target?: EventTarget) => void }
>;

export function useIntro(): UseIntroResult {
	const { data, actions } = useStartAPetitionPageState();
	const {
		data: {
			featureConfigs: { sapIntroCampaignMelones },
		},
	} = useStartAPetitionPageData();
	const track = useTracking();
	const triggerFocusFallback = useFocusFallbackManualTrigger();

	const copy = toCampaignCopy(sapIntroCampaignMelones);
	const { imageUrl, imageAlt } = sapIntroCampaignMelones;
	const image = imageUrl ? { path: imageUrl, alt: imageAlt } : undefined;

	// A draft is "the user's" when there's draft content and no AI-authored text.
	// We key on ask/description (not recommendedDraft's truthiness) because image
	// regeneration writes recommendedDraft.image for write-my-own users too, which
	// would otherwise wipe their work in the write_my_own branch below.
	const hasAiContent = !!(data.draft?.recommendedDraft?.ask || data.draft?.recommendedDraft?.description);
	const hasUserDraft = !!((data.draft?.petitionDraft || data.draft?.editingDraft) && !hasAiContent);

	const submit = useCallback(
		(flow: Flow, target?: EventTarget) => {
			const stepData = { cta: flow, campaign: 'melones' };
			void track('sap_step_submit', {
				step: data.normalizedStep,
				step_data: stepData,
				step_data_raw: JSON.stringify(stepData),
			});
			if (flow === 'write_my_own') {
				const draft = !hasUserDraft
					? { recommendedDraft: null, petitionDraft: null, editingDraft: null, metadata: { imageJobId: null } }
					: {};
				actions.send({ type: 'submit', target: 'user', draft });
			} else {
				actions.send({ type: 'submit', target: 'ai' });
			}

			// The campaign primary button isn't a PrimaryActionButton, so it hands its target here for the focus fallback,
			// triggered after the transition like PrimaryActionButton does.
			if (target) triggerFocusFallback(target);
		},
		[actions, data.normalizedStep, hasUserDraft, track, triggerFocusFallback],
	);

	return {
		data: { copy, image },
		actions: {
			submit,
		},
	};
}
